import { getContext, setContext } from 'svelte';
import { Sfx } from '../audio/sounds';
import { watchSystemMotion } from '../platform/reduced-motion';
import type { BrowserStore } from '../platform/storage';
import { applyTheme, DEFAULT_THEME, getTheme, type Theme } from './registry';

type PreferenceStore = Pick<BrowserStore, 'get' | 'set'>;

/**
 * App-wide presentation preferences: the active theme pack and the audio/motion
 * toggles, on unless explicitly turned off. One instance is created by the host with its `BrowserStore` and shared
 * through context; `initialize()` mirrors preferences to the audio engine, the
 * document and storage for the host's lifetime.
 */
export class Appearance {
  #theme = $state<Theme>(DEFAULT_THEME);
  effects = $state(false);
  // Keep the existing BGM preference's storage key ("ambience").
  music = $state(false);
  ambience = $state(false);
  animations = $state(false);
  #systemMotion = $state(true);

  constructor(private readonly store: PreferenceStore) {
    this.effects = store.get('effects') !== '0';
    this.music = store.get('ambience') !== '0';
    this.ambience = store.get('environment') !== '0';
    this.animations = store.get('animations') !== '0';
  }

  get theme(): Theme {
    return this.#theme;
  }

  /** The one motion preference: the in-app Motion setting and the OS both allow animation. */
  get motionAllowed(): boolean {
    return this.animations && this.#systemMotion;
  }

  selectTheme = (id: string | null): void => {
    const theme = getTheme(id);
    applyTheme(theme);
    Sfx.configure(theme.sounds, theme.haptics);
    this.#theme = theme;
    this.store.set('theme.selected', theme.id);
  };

  /** Returns cleanup for hosts that unmount/remount the application. */
  initialize(): () => void {
    const stop = $effect.root(() => {
      $effect(() => {
        Sfx.setEffects(this.effects);
        this.store.set('effects', this.effects ? '1' : '0');
      });
      $effect(() => {
        Sfx.setMusic(this.music);
        this.store.set('ambience', this.music ? '1' : '0');
      });
      $effect(() => {
        Sfx.setAmbience(this.ambience);
        this.store.set('environment', this.ambience ? '1' : '0');
      });
      $effect(() => {
        this.store.set('animations', this.animations ? '1' : '0');
      });
      $effect(() => {
        document.documentElement.setAttribute('data-reduced-motion', String(!this.motionAllowed));
      });
    });
    const stopWatchingMotion = watchSystemMotion((allowed) => (this.#systemMotion = allowed));
    this.selectTheme(this.store.get('theme.selected'));
    return () => {
      stop();
      stopWatchingMotion();
      Sfx.dispose();
    };
  }
}

const KEY = Symbol('spellie.appearance');

export function provideAppearance(appearance: Appearance): Appearance {
  return setContext(KEY, appearance);
}

export function getAppearance(): Appearance {
  const appearance = getContext<Appearance | undefined>(KEY);
  if (!appearance) throw new Error('Appearance context is missing; the root must provide it');
  return appearance;
}
